import React, { useState } from "react";
import { Dog, Cat, Star, Heart, Sun, Moon, Cloud, Fish, Rocket, RotateCcw } from "lucide-react";

// Nine friendly icons in a fixed order → keeps the grid predictable across sessions.
export const PIN_ICONS = [
  { key: "dog", label: "Dog", Icon: Dog, color: "#E07A5F" },
  { key: "cat", label: "Cat", Icon: Cat, color: "#3D5A80" },
  { key: "star", label: "Star", Icon: Star, color: "#F2CC8F" },
  { key: "heart", label: "Heart", Icon: Heart, color: "#E53E3E" },
  { key: "sun", label: "Sun", Icon: Sun, color: "#F2CC8F" },
  { key: "moon", label: "Moon", Icon: Moon, color: "#3D5A80" },
  { key: "cloud", label: "Cloud", Icon: Cloud, color: "#4A5568" },
  { key: "fish", label: "Fish", Icon: Fish, color: "#2B7A78" },
  { key: "rocket", label: "Rocket", Icon: Rocket, color: "#2B7A78" },
];

export const PIN_ICON_MAP = Object.fromEntries(PIN_ICONS.map((i) => [i.key, i]));

/**
 * PicturePinInput
 * - onComplete(pinArray) fires after the user picks 3 icons.
 * - The parent decides what to do (verify vs. confirm-first-entry).
 * - Provides a Reset button and progress dots.
 */
export default function PicturePinInput({ onComplete, disabled = false, testIdPrefix = "pin" }) {
  const [picked, setPicked] = useState([]);

  const handlePick = (key) => {
    if (disabled) return;
    const next = [...picked, key];
    setPicked(next);
    if (next.length === 3) {
      onComplete(next);
    }
  };

  const reset = () => setPicked([]);

  return (
    <div className="w-full" data-testid={`${testIdPrefix}-input`}>
      {/* Progress dots */}
      <div className="flex items-center justify-center gap-3 mb-4" data-testid={`${testIdPrefix}-progress`}>
        {[0, 1, 2].map((i) => {
          const filled = i < picked.length;
          return (
            <span
              key={i}
              className="rounded-full"
              style={{
                width: 16,
                height: 16,
                background: filled ? "var(--tm-brand)" : "var(--tm-bg-3)",
                border: "2px solid var(--tm-border)",
                transition: "background-color 160ms ease",
              }}
              aria-hidden
            />
          );
        })}
      </div>

      {/* 3x3 grid */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4" data-testid={`${testIdPrefix}-grid`}>
        {PIN_ICONS.map(({ key, label, Icon, color }) => (
          <button
            key={key}
            type="button"
            onClick={() => handlePick(key)}
            disabled={disabled || picked.length >= 3}
            className="tm-card p-4 sm:p-6 flex flex-col items-center gap-2 hover:border-[color:var(--tm-brand)] transition-colors tm-focus-ring"
            style={{ minHeight: 96 }}
            aria-label={`Pick ${label}`}
            data-testid={`${testIdPrefix}-icon-${key}`}
          >
            <Icon size={40} strokeWidth={2.2} style={{ color }} />
            <span className="text-sm font-bold">{label}</span>
          </button>
        ))}
      </div>

      <div className="flex justify-center mt-4">
        <button
          type="button"
          onClick={reset}
          className="tm-btn tm-btn-ghost"
          disabled={disabled || picked.length === 0}
          data-testid={`${testIdPrefix}-reset`}
        >
          <RotateCcw size={16} /> Start over
        </button>
      </div>
    </div>
  );
}
